import React, { useState, useEffect } from 'react';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useToast } from '../common/Toast';
import {
  Smartphone,
  Download,
  Share,
  PlusSquare,
  CheckCircle2,
  Sparkles,
  Zap,
  Bell,
  HelpCircle,
  X,
  ExternalLink,
  Laptop,
  Check
} from 'lucide-react';

export const PwaInstallCtaSection: React.FC = () => {
  const { isInstallable, isInstalled, isIOS, isAndroid, promptInstall } = usePwaInstall();
  const { showToast } = useToast();
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [activeGuideTab, setActiveGuideTab] = useState<'android' | 'ios' | 'desktop'>('android');

  // Set default tab based on user device
  useEffect(() => {
    if (isIOS) setActiveGuideTab('ios');
    else if (isAndroid) setActiveGuideTab('android');
    else setActiveGuideTab('desktop');
  }, [isIOS, isAndroid]);

  // Lock body scroll when modal is open (strict project rule)
  useEffect(() => {
    if (isGuideOpen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isGuideOpen]);

  const handleInstallClick = async () => {
    if (isInstalled) {
      showToast('info', 'Aplikasi Telah Dipasang', 'Portal ASMM sudah terpasang pada peranti anda.');
      return;
    }

    const result = await promptInstall();
    if (result === 'accepted') {
      showToast('success', 'Pemasangan Berjaya!', 'Aplikasi ASMM telah ditambah ke skrin utama anda.');
    } else if (result === 'manual_guide' || isIOS) {
      // For iOS Safari or when native browser prompt is unavailable, show step-by-step guide
      setIsGuideOpen(true);
    }
  };

  return (
    <>
      <section className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-6 relative overflow-hidden">
        {/* Decorative Top-Right Martial Ring Accent */}
        <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-gradient-to-br from-red-500/10 via-amber-500/5 to-transparent pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          {/* Left Column: Visual Icon & Details */}
          <div className="flex items-start gap-4 sm:gap-5 flex-1">
            <div className="relative shrink-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#0B0F19] border border-slate-800 shadow-md flex items-center justify-center overflow-hidden group">
                <img
                  src="/icons/icon-192.png"
                  alt="ASMM App Icon"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 p-1 bg-amber-500 text-slate-950 rounded-full shadow-xs ring-2 ring-white flex items-center justify-center">
                <Smartphone className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="space-y-1.5 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-black uppercase tracking-wider text-asmm-red bg-red-50 border border-red-200 px-2.5 py-0.5 rounded-full">
                  Pintasan Skrin Utama (PWA)
                </span>
                {isInstalled && (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Terpasang
                  </span>
                )}
              </div>

              <h3 className="text-base sm:text-xl font-extrabold text-slate-900 leading-tight">
                Pasang Aplikasi ASMM &amp; Cipta Pintasan
              </h3>

              <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
                Akses pantas tanpa taip URL di pelayar, semak status yuran &amp; bengkung silat, serta terima peringatan latihan gelanggang terus pada skrin utama telefon pintar anda.
              </p>
            </div>
          </div>

          {/* Right Column: High-Contrast CTA Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 shrink-0">
            {isInstalled ? (
              <div className="px-5 py-3 rounded-full bg-slate-100 text-slate-800 font-bold text-xs flex items-center justify-center gap-2 border border-slate-200">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>Aplikasi Aktif Pada Peranti</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleInstallClick}
                className="px-6 py-3 rounded-full bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer border-none"
                title="Pasang aplikasi web PSSASMM ke skrin utama anda"
              >
                <Download className="w-4 h-4 text-amber-300" />
                <span>Pasang Aplikasi Sekarang</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="px-4 py-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-extrabold text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer border border-slate-200/80"
              title="Lihat cara memasang aplikasi di telefon Android, iPhone atau PC"
            >
              <HelpCircle className="w-3.5 h-3.5 text-slate-600" />
              <span>Cara Pasang</span>
            </button>
          </div>
        </div>

        {/* Floating Feature Pills Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-slate-50 border border-slate-100/80">
            <div className="p-2 rounded-xl bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
              <Zap className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-900">Akses Pantas 1-Klik</p>
              <p className="text-[10px] text-slate-500">Ikon di skrin utama tanpa muat turun Play Store</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-slate-50 border border-slate-100/80">
            <div className="p-2 rounded-xl bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
              <Bell className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-900">Peringatan Latihan Gelanggang</p>
              <p className="text-[10px] text-slate-500">Notifikasi RSVP 20 jam sebelum sesi bermula</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-slate-50 border border-slate-100/80">
            <div className="p-2 rounded-xl bg-red-50 text-asmm-red border border-red-200 shrink-0">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-900">Ringan &amp; Jimat Storan</p>
              <p className="text-[10px] text-slate-500">Kurang dari 3MB, berfungsi laju &amp; selamat</p>
            </div>
          </div>
        </div>
      </section>

      {/* Manual Installation Guided Modal (Locking Body Scroll) */}
      {isGuideOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-fade-in no-print">
          <div
            className="relative w-full max-w-lg bg-white rounded-[2rem] border border-slate-100 shadow-[0_25px_70px_-15px_rgba(15,23,42,0.25)] p-6 sm:p-7 space-y-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 text-white flex items-center justify-center shadow-md shadow-red-500/20 shrink-0">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-extrabold text-slate-900">
                    Panduan Pasang Aplikasi ASMM
                  </h4>
                  <p className="text-[11px] text-slate-500 font-medium">
                    Tambah pintasan ke skrin utama peranti anda
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsGuideOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100/90 hover:bg-slate-200 text-slate-400 hover:text-slate-700 flex items-center justify-center transition-all cursor-pointer backdrop-blur-xs"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Platform Selector Tabs */}
            <div className="grid grid-cols-3 gap-1.5 p-1.5 bg-slate-100/90 rounded-2xl border border-slate-200/50">
              <button
                type="button"
                onClick={() => setActiveGuideTab('android')}
                className={`py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeGuideTab === 'android'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Android
              </button>

              <button
                type="button"
                onClick={() => setActiveGuideTab('ios')}
                className={`py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeGuideTab === 'ios'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                iPhone / iPad
              </button>

              <button
                type="button"
                onClick={() => setActiveGuideTab('desktop')}
                className={`py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeGuideTab === 'desktop'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Komputer / PC
              </button>
            </div>

            {/* Tab 1: Android (Chrome / Brave / Samsung Internet) */}
            {activeGuideTab === 'android' && (
              <div className="space-y-3.5 text-xs text-slate-700">
                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      1
                    </span>
                    <p className="pt-0.5">
                      Buka portal ini menggunakan pelayar <strong>Google Chrome</strong> di telefon Android anda.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      2
                    </span>
                    <p className="pt-0.5">
                      Tekan butang menu <strong>tiga titik (⋮)</strong> di penjuru atas sebelah kanan skrin.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      3
                    </span>
                    <p className="pt-0.5">
                      Pilih <strong>"Install app"</strong> atau <strong>"Add to Home screen"</strong> (Tambah ke skrin utama).
                    </p>
                  </div>
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={async () => {
                      setIsGuideOpen(false);
                      await promptInstall();
                    }}
                    className="w-full py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-4 h-4 text-amber-300" />
                    <span>Cuba Pasang Sekarang</span>
                  </button>
                </div>
              </div>
            )}

            {/* Tab 2: iOS / Safari (iPhone & iPad) */}
            {activeGuideTab === 'ios' && (
              <div className="space-y-3.5 text-xs text-slate-700">
                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      1
                    </span>
                    <p className="pt-0.5">
                      Buka pautan ini dalam pelayar <strong>Safari</strong> (bukan Chrome atau dalam aplikasi media sosial).
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      2
                    </span>
                    <div className="space-y-1 pt-0.5">
                      <p>
                        Tekan butang <strong>Kongsi / Share</strong> di bar navigasi bahagian bawah:
                      </p>
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-lg border border-slate-200 text-slate-900 font-bold">
                        <Share className="w-3.5 h-3.5 text-blue-600" />
                        <span>Ikon Kotak Berpanah Atas</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      3
                    </span>
                    <div className="space-y-1 pt-0.5">
                      <p>
                        Tatal ke bawah dan tekan pilihan:
                      </p>
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-lg border border-slate-200 text-slate-900 font-bold">
                        <PlusSquare className="w-3.5 h-3.5 text-slate-800" />
                        <span>"Add to Home Screen" (Tambah ke Skrin Utama)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-slate-500 italic text-center">
                  Ikon rasmi ASMM akan terus muncul di skrin utama iPhone anda seperti aplikasi biasa.
                </p>
              </div>
            )}

            {/* Tab 3: Desktop (Chrome, Edge, Brave) */}
            {activeGuideTab === 'desktop' && (
              <div className="space-y-3.5 text-xs text-slate-700">
                <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      1
                    </span>
                    <p className="pt-0.5">
                      Buka portal pada pelayar <strong>Chrome</strong> atau <strong>Microsoft Edge</strong>.
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      2
                    </span>
                    <p className="pt-0.5">
                      Lihat pada <strong>bar alamat URL</strong> di bahagian atas sebelah kanan, tekan ikon pasang <Laptop className="w-3.5 h-3.5 inline mx-1 text-slate-700" /> (Komputer / App).
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-asmm-red text-white font-extrabold flex items-center justify-center shrink-0 text-xs">
                      3
                    </span>
                    <p className="pt-0.5">
                      Tekan <strong>"Install"</strong> untuk membuka portal dalam tetingkap bebas tanpa bar pelayar.
                    </p>
                  </div>
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={async () => {
                      setIsGuideOpen(false);
                      await promptInstall();
                    }}
                    className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-xl shadow-xs flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-4 h-4 text-amber-300" />
                    <span>Pasang Pada Komputer Ini</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
